import { useState, useEffect, useRef } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useTheme } from "../../contexts/ThemeContext";
import { useAuth } from "../../contexts/AuthContext";

const NAV_LINKS = [
  { label: "Home", path: "/" },
  { label: "Product", path: "/product" },
  { label: "Showcase", path: "/showcase" },
  { label: "Demonstration", path: "/demonstration" },
  { label: "Contact Us", path: "/contact" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const userMenuRef = useRef<HTMLDivElement>(null);
  const location = useLocation();
  const navigate = useNavigate();
  const { isDark, toggleTheme } = useTheme();
  const { user, profile, signOut } = useAuth();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target as Node)) {
        setUserMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const navActive = isDark ? "text-white" : "text-[#111111]";
  const navInactive = isDark
    ? "text-[#888888] hover:text-white"
    : "text-[#666666] hover:text-[#111111]";
  const scrolledBg = isDark
    ? "bg-black/90 backdrop-blur-xl border-b border-white/5"
    : "bg-white/95 backdrop-blur-xl border-b border-black/8";

  const handleSignOut = async () => {
    setUserMenuOpen(false);
    await signOut();
    navigate("/");
  };

  const isPro = profile?.plan === "pro";
  const userInitial = user?.email?.[0]?.toUpperCase() ?? "U";
  const avatarUrl = profile?.avatar_url ?? null;
  const displayName = profile?.display_name || user?.email?.split("@")[0] || null;

  const scrollToEmail = () => {
    setMenuOpen(false);
    const focusInput = () => {
      const section = document.getElementById("early-access-section");
      const input = document.getElementById("early-access-email") as HTMLInputElement | null;
      if (section) {
        section.scrollIntoView({ behavior: "smooth", block: "center" });
      }
      setTimeout(() => input?.focus(), 500);
    };

    if (location.pathname !== "/") {
      navigate("/");
      setTimeout(focusInput, 300);
    } else {
      focusInput();
    }
  };

  return (
    <>
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          scrolled ? scrolledBg : "bg-transparent"
        }`}
      >
        <nav className="flex items-center justify-between px-8 lg:px-16 h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 cursor-pointer">
            <img
              src="https://public.readdy.ai/ai/img_res/9279aaa0-a972-448e-b5a8-cead4885da19.png"
              alt="Trackly logo"
              className={`h-8 w-auto object-contain transition-all duration-300 ${!isDark ? "invert" : ""}`}
            />
          </Link>

          {/* Desktop Nav */}
          <ul className="hidden md:flex items-center gap-10">
            {NAV_LINKS.map((link) => (
              <li key={link.path}>
                <Link
                  to={link.path}
                  className={`font-inter text-sm transition-colors duration-200 cursor-pointer whitespace-nowrap ${
                    location.pathname === link.path ? navActive : navInactive
                  }`}
                >
                  {link.label}
                </Link>
              </li>
            ))}
          </ul>

          {/* CTAs + Theme Toggle */}
          <div className="hidden md:flex items-center gap-3">
            {/* Light/Dark Toggle Pill */}
            <button
              onClick={toggleTheme}
              aria-label="Toggle theme"
              className={`relative flex items-center w-[56px] h-[28px] rounded-full cursor-pointer transition-all duration-300 border ${
                isDark
                  ? "bg-[#1a1a1a] border-white/10"
                  : "bg-[#f0f0f0] border-black/10"
              }`}
            >
              <span
                className={`absolute left-[6px] w-4 h-4 flex items-center justify-center transition-opacity duration-200 ${
                  isDark ? "opacity-30" : "opacity-100"
                }`}
              >
                <i className="ri-sun-line text-[11px] text-[#f59e0b]" />
              </span>
              <span
                className={`absolute right-[6px] w-4 h-4 flex items-center justify-center transition-opacity duration-200 ${
                  isDark ? "opacity-100" : "opacity-30"
                }`}
              >
                <i className="ri-moon-line text-[11px] text-[#a78bfa]" />
              </span>
              <span
                className={`absolute top-[3px] w-[22px] h-[22px] rounded-full shadow transition-all duration-300 flex items-center justify-center ${
                  isDark
                    ? "translate-x-[28px] bg-[#111111] border border-white/10"
                    : "translate-x-[3px] bg-white border border-black/8"
                }`}
              >
                {isDark ? (
                  <i className="ri-moon-fill text-[10px] text-[#a78bfa]" />
                ) : (
                  <i className="ri-sun-fill text-[10px] text-[#f59e0b]" />
                )}
              </span>
            </button>

            {user ? (
              <div className="relative" ref={userMenuRef}>
                <button
                  type="button"
                  onClick={() => setUserMenuOpen(!userMenuOpen)}
                  className="flex items-center gap-2 cursor-pointer"
                  aria-label="User menu"
                >
                  <div className={`w-8 h-8 rounded-full flex-shrink-0 overflow-hidden flex items-center justify-center font-inter font-semibold text-sm ${isDark ? "bg-white/10 text-white" : "bg-black/10 text-[#111]"}`}>
                    {avatarUrl ? (
                      <img src={avatarUrl} alt="Profile" className="w-full h-full object-cover rounded-full" />
                    ) : (
                      userInitial
                    )}
                  </div>
                  {isPro && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold font-inter bg-amber-500/15 text-amber-500 border border-amber-500/20 whitespace-nowrap">
                      <i className="ri-vip-crown-fill text-[8px]" />
                      PRO
                    </span>
                  )}
                  <i className={`ri-arrow-down-s-line text-sm transition-transform duration-200 ${userMenuOpen ? "rotate-180" : ""} ${isDark ? "text-[#666]" : "text-[#999]"}`} />
                </button>

                {userMenuOpen && (
                  <div className={`absolute right-0 top-full mt-2 w-48 rounded-xl border py-1.5 z-50 ${isDark ? "bg-[#111] border-white/10" : "bg-white border-black/10"}`}>
                    <div className={`px-3 py-2.5 border-b mb-1 ${isDark ? "border-white/5" : "border-black/5"}`}>
                      {displayName && (
                        <p className={`font-inter text-sm font-medium truncate mb-0.5 ${isDark ? "text-white" : "text-[#111]"}`}>{displayName}</p>
                      )}
                      <p className={`font-inter text-xs truncate ${isDark ? "text-[#444]" : "text-[#bbb]"}`}>{user.email}</p>
                    </div>
                    <Link
                      to="/dashboard"
                      onClick={() => setUserMenuOpen(false)}
                      className={`flex items-center gap-2 px-3 py-2 font-inter text-sm cursor-pointer transition-colors duration-150 ${isDark ? "text-[#aaa] hover:text-white hover:bg-white/5" : "text-[#555] hover:text-[#111] hover:bg-black/4"}`}
                    >
                      <i className="ri-dashboard-line text-sm" />
                      Dashboard
                    </Link>
                    <button
                      type="button"
                      onClick={handleSignOut}
                      className={`w-full flex items-center gap-2 px-3 py-2 font-inter text-sm cursor-pointer transition-colors duration-150 text-left ${isDark ? "text-[#aaa] hover:text-white hover:bg-white/5" : "text-[#555] hover:text-[#111] hover:bg-black/4"}`}
                    >
                      <i className="ri-logout-box-r-line text-sm" />
                      Sign Out
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <Link
                to="/dashboard"
                className={`font-inter text-sm transition-colors duration-200 cursor-pointer whitespace-nowrap ${
                  isDark ? "text-[#888888] hover:text-white" : "text-[#666666] hover:text-[#111111]"
                }`}
              >
                Sign In
              </Link>
            )}

            <button
              onClick={scrollToEmail}
              className={`font-inter text-sm px-5 py-2 rounded-full font-medium transition-colors duration-200 cursor-pointer whitespace-nowrap ${
                isDark
                  ? "bg-white text-black hover:bg-[#e0e0e0]"
                  : "bg-[#111111] text-white hover:bg-[#2a2a2a]"
              }`}
            >
              Join us
            </button>
          </div>

          {/* Mobile Menu Toggle */}
          <div className="md:hidden flex items-center gap-2">
            <button
              onClick={toggleTheme}
              aria-label="Toggle theme"
              className={`relative flex items-center w-[48px] h-[24px] rounded-full cursor-pointer transition-all duration-300 border ${
                isDark
                  ? "bg-[#1a1a1a] border-white/10"
                  : "bg-[#f0f0f0] border-black/10"
              }`}
            >
              <span className={`absolute left-[5px] w-3.5 h-3.5 flex items-center justify-center transition-opacity duration-200 ${isDark ? "opacity-30" : "opacity-100"}`}>
                <i className="ri-sun-line text-[10px] text-[#f59e0b]" />
              </span>
              <span className={`absolute right-[5px] w-3.5 h-3.5 flex items-center justify-center transition-opacity duration-200 ${isDark ? "opacity-100" : "opacity-30"}`}>
                <i className="ri-moon-line text-[10px] text-[#a78bfa]" />
              </span>
              <span className={`absolute top-[2.5px] w-[19px] h-[19px] rounded-full shadow transition-all duration-300 flex items-center justify-center ${isDark ? "translate-x-[23px] bg-[#111111] border border-white/10" : "translate-x-[2.5px] bg-white border border-black/8"}`}>
                {isDark ? (
                  <i className="ri-moon-fill text-[9px] text-[#a78bfa]" />
                ) : (
                  <i className="ri-sun-fill text-[9px] text-[#f59e0b]" />
                )}
              </span>
            </button>

            {/* Avatar / Menu Toggle — replaces hamburger */}
            <div className="flex flex-col items-center gap-0.5 mt-2">
              <button
                className="relative w-10 h-10 rounded-full cursor-pointer flex-shrink-0 transition-all duration-200 focus:outline-none"
                onClick={() => setMenuOpen(!menuOpen)}
                aria-label="Toggle menu"
              >
                {/* Ring — glows when open */}
                <span
                  className={`absolute inset-0 rounded-full border-2 transition-all duration-200 z-10 ${
                    menuOpen
                      ? isDark
                        ? "border-white/70"
                        : "border-[#111]/60"
                      : isDark
                      ? "border-white/20"
                      : "border-black/15"
                  }`}
                />

                {/* Avatar content */}
                <span className="absolute inset-0 rounded-full overflow-hidden">
                  {user ? (
                    avatarUrl ? (
                      <img
                        src={avatarUrl}
                        alt="Profile"
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <span
                        className={`w-full h-full flex items-center justify-center font-inter font-semibold text-sm ${
                          isDark ? "bg-white/10 text-white" : "bg-black/10 text-[#111]"
                        }`}
                      >
                        {userInitial}
                      </span>
                    )
                  ) : (
                    <img
                      src="https://public.readdy.ai/ai/img_res/fffcc25b1ecb4da2a90187051ba61408.jpg"
                      alt="Menu"
                      className="w-full h-full object-cover"
                    />
                  )}
                </span>

                {/* Close indicator badge when menu is open */}
                {menuOpen && (
                  <span
                    className={`absolute -top-0.5 -right-0.5 w-4 h-4 rounded-full flex items-center justify-center z-20 border ${
                      isDark
                        ? "bg-[#111] border-white/20"
                        : "bg-white border-black/15"
                    }`}
                  >
                    <i className={`ri-close-line text-[9px] ${isDark ? "text-white" : "text-[#111]"}`} />
                  </span>
                )}
              </button>
              <span
                className={`font-inter text-[9px] tracking-wide transition-colors duration-200 ${
                  menuOpen
                    ? isDark ? "text-white/70" : "text-[#111]/60"
                    : isDark ? "text-white/30" : "text-black/30"
                }`}
              >
                Menu
              </span>
            </div>
          </div>
        </nav>

        {/* Mobile Menu — always mounted, animated with max-height */}
        <div
          className={`md:hidden overflow-hidden transition-all duration-300 ease-in-out border-t ${
            menuOpen ? "max-h-[600px] opacity-100" : "max-h-0 opacity-0"
          } ${
            isDark
              ? "bg-black/95 backdrop-blur-xl border-white/5"
              : "bg-white/97 backdrop-blur-xl border-black/8"
          }`}
          style={{ transition: "max-height 0.35s ease, opacity 0.25s ease" }}
        >
          <div className="px-8 py-6 flex flex-col gap-1">
            {/* All nav links listed directly */}
            {NAV_LINKS.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                onClick={() => setMenuOpen(false)}
                className={`flex items-center gap-3 px-3 py-3 rounded-xl font-inter text-sm transition-colors duration-150 cursor-pointer ${
                  location.pathname === link.path
                    ? isDark
                      ? "bg-white/8 text-white font-medium"
                      : "bg-black/6 text-[#111] font-medium"
                    : isDark
                    ? "text-[#888] hover:text-white hover:bg-white/5"
                    : "text-[#666] hover:text-[#111] hover:bg-black/4"
                }`}
              >
                <i className={`text-base flex-shrink-0 ${
                  link.path === "/" ? "ri-home-3-line" :
                  link.path === "/product" ? "ri-box-3-line" :
                  link.path === "/showcase" ? "ri-gallery-view-2" :
                  link.path === "/demonstration" ? "ri-play-circle-line" :
                  "ri-mail-line"
                }`} />
                {link.label}
                {location.pathname === link.path && (
                  <span className={`ml-auto w-1.5 h-1.5 rounded-full flex-shrink-0 ${isDark ? "bg-white" : "bg-[#111]"}`} />
                )}
              </Link>
            ))}

            {/* Divider */}
            <div className={`my-3 border-t ${isDark ? "border-white/8" : "border-black/8"}`} />

            {/* User section */}
            <div className="flex flex-col gap-2">
              {user ? (
                <>
                  <div className={`flex items-center gap-3 px-3 py-2.5 rounded-xl ${isDark ? "bg-white/4" : "bg-black/3"}`}>
                    <div className={`w-8 h-8 rounded-full overflow-hidden flex items-center justify-center font-inter font-semibold text-xs flex-shrink-0 ${isDark ? "bg-white/10 text-white" : "bg-black/10 text-[#111]"}`}>
                      {avatarUrl ? (
                        <img src={avatarUrl} alt="Profile" className="w-full h-full object-cover rounded-full" />
                      ) : (
                        userInitial
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className={`font-inter text-xs font-medium truncate ${isDark ? "text-white" : "text-[#111]"}`}>
                        {displayName}
                      </p>
                      <p className={`font-inter text-[10px] truncate ${isDark ? "text-[#555]" : "text-[#aaa]"}`}>
                        {user.email}
                      </p>
                    </div>
                    {isPro && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] font-semibold font-inter bg-amber-500/15 text-amber-500 border border-amber-500/20 flex-shrink-0">
                        <i className="ri-vip-crown-fill text-[7px]" />
                        PRO
                      </span>
                    )}
                  </div>
                  <Link
                    to="/dashboard"
                    onClick={() => setMenuOpen(false)}
                    className={`flex items-center gap-3 px-3 py-3 rounded-xl font-inter text-sm transition-colors duration-150 cursor-pointer ${isDark ? "text-[#888] hover:text-white hover:bg-white/5" : "text-[#666] hover:text-[#111] hover:bg-black/4"}`}
                  >
                    <i className="ri-dashboard-line text-base" />
                    Dashboard
                  </Link>
                  <button
                    type="button"
                    onClick={() => { setMenuOpen(false); handleSignOut(); }}
                    className={`flex items-center gap-3 px-3 py-3 rounded-xl font-inter text-sm transition-colors duration-150 cursor-pointer text-left w-full ${isDark ? "text-[#888] hover:text-white hover:bg-white/5" : "text-[#666] hover:text-[#111] hover:bg-black/4"}`}
                  >
                    <i className="ri-logout-box-r-line text-base" />
                    Sign Out
                  </button>
                </>
              ) : (
                <Link
                  to="/dashboard"
                  onClick={() => setMenuOpen(false)}
                  className={`flex items-center gap-3 px-3 py-3 rounded-xl font-inter text-sm transition-colors duration-150 cursor-pointer ${isDark ? "text-[#888] hover:text-white hover:bg-white/5" : "text-[#666] hover:text-[#111] hover:bg-black/4"}`}
                >
                  <i className="ri-user-line text-base" />
                  Sign In
                </Link>
              )}
              <button
                onClick={scrollToEmail}
                className={`flex items-center justify-center gap-2 font-inter text-sm px-5 py-3 rounded-xl font-medium w-full cursor-pointer whitespace-nowrap transition-colors duration-200 ${
                  isDark
                    ? "bg-white text-black hover:bg-[#e0e0e0]"
                    : "bg-[#111111] text-white hover:bg-[#2a2a2a]"
                }`}
              >
                Join us
              </button>
            </div>
          </div>
        </div>
      </header>
    </>
  );
}
